import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { getItemWebStorage, setItemWebStorage } from "../../../../features/webStorageSaver";
import { setMapLayersOpacityState } from "../../../../stateManagement/reducers";
import { Slider } from "primereact/slider";
import { classNames } from "primereact/utils";
import { auxiliaryLayersStructure } from "../../../../pages/mapSettings/mapSettingsFunctions";
import { store } from "../../../../stateManagement/store";

// Виджет управления прозрачностью дополнительных слоёв
export default function LayersOpacityOverlay() {
    const dispatch = useDispatch();
    const additionalLayersDisplayState: any = useAppSelector(state => state.mapSettings.additionalLayersDisplayState);
    const additionalLayersOpacityState: any = useAppSelector(state => state.mapSettings.additionalLayersOpacityState);
    const auxiliaryLayersDisplayState: any = useAppSelector(state => state.mapSettings.auxiliaryLayersDisplayState);
    const auxiliaryLayersOpacityState: any = useAppSelector(state => state.mapSettings.auxiliaryLayersOpacityState);

    const [isOpen, setIsOpen] = useState(false);

    // Структура слоёв из конфигурации (id -> caption)
    const { mapSettingsAdditionalLayers } = getItemWebStorage('currentConfig') ?? {};
    const layersDict: Record<string, string> = useMemo(() => {
        const dict: Record<string, string> = {};
        mapSettingsAdditionalLayers?.layers?.forEach((item: any) => {
            const key = item?.description?.id ?? item?.name;
            if (key) dict[key] = item?.label ?? key;
        });
        return dict;
    }, [mapSettingsAdditionalLayers]);

    // Список включённых дополнительных слоёв
    const enabledAdditionalLayerKeys = useMemo(() => {
        return Object.keys(additionalLayersDisplayState ?? {}).filter(k => additionalLayersDisplayState?.[k]);
    }, [additionalLayersDisplayState]);

    // Список включённых вспомогательных слоёв (из известных)
    const enabledAuxiliaryLayerKeys = useMemo(() => {
        const keys = Object.keys(auxiliaryLayersDisplayState ?? {}).filter(k => auxiliaryLayersDisplayState?.[k]);
        // показываем только известные слои со слайдами
        return keys.filter(k => ['esriWorldImagery', 'indorAvia', 'allObjects'].includes(k));
    }, [auxiliaryLayersDisplayState]);

    // Если нет включённых слоёв ни там, ни там — ничего не рендерим
    if ((enabledAdditionalLayerKeys?.length ?? 0) === 0 && (enabledAuxiliaryLayerKeys?.length ?? 0) === 0) return null;

    function onChangeLayerOpacity(layerName: string, newOpacity01: number) {
        // Быстрый отклик — обновляем только store, без записи в localStorage
        const currentTypeOpacityState = store.getState().mapSettings?.additionalLayersOpacityState ?? {};
        const newLayersOpacityState = { ...currentTypeOpacityState, [layerName]: newOpacity01 };
        dispatch(setMapLayersOpacityState({ layersType: 'additional', layersState: newLayersOpacityState }));
    }

    function commitAdditionalOpacity() {
        const reduxState = store.getState().mapSettings;
        const ls = getItemWebStorage('mapSettings') ?? {};
        setItemWebStorage('mapSettings', { ...ls, additionalLayersOpacityState: reduxState?.additionalLayersOpacityState ?? {} });
    }

    function onChangeAuxLayerOpacity(layerName: string, newOpacity01: number) {
        const currentTypeOpacityState = store.getState().mapSettings?.auxiliaryLayersOpacityState ?? {};
        const newLayersOpacityState = { ...currentTypeOpacityState, [layerName]: newOpacity01 };
        dispatch(setMapLayersOpacityState({ layersType: 'auxiliary', layersState: newLayersOpacityState }));
    }

    function commitAuxOpacity() {
        const reduxState = store.getState().mapSettings;
        const ls = getItemWebStorage('mapSettings') ?? {};
        setItemWebStorage('mapSettings', { ...ls, auxiliaryLayersOpacityState: reduxState?.auxiliaryLayersOpacityState ?? {} });
    }

    const overlayClass = classNames({ "layers-opacity-overlay": true });

    return (
        <div className={overlayClass}>
            <div className="buttons-div">
                <div className="filters-button-div" onClick={() => setIsOpen(!isOpen)}>
                    <span>Прозрачность слоёв</span>
                </div>
            </div>
            {isOpen &&
                <div className="filters-div">
                    {enabledAuxiliaryLayerKeys.length > 0 && (
                        <>
                            <div className="layers-type-section">
                                <span>ВСПОМОГАТЕЛЬНЫЕ СЛОИ</span>
                            </div>
                            <div className="layers-list">
                                {enabledAuxiliaryLayerKeys.map((layerName: string) => (
                                    <div key={layerName} className='layers-list-item'>
                                        <label className='checkbox-label'>{auxiliaryLayersStructure?.[layerName] ?? layerName}</label>
                                        <div className='opacity-control'>
                                            <Slider
                                                value={Math.round((auxiliaryLayersOpacityState?.[layerName] ?? 1) * 100)}
                                                onChange={(e: any) => onChangeAuxLayerOpacity(layerName, (e.value ?? 100) / 100)}
                                                onSlideEnd={commitAuxOpacity}
                                                step={1} min={0} max={100}
                                            />
                                            <span className='opacity-percent'>{Math.round((auxiliaryLayersOpacityState?.[layerName] ?? 1) * 100)}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    {enabledAdditionalLayerKeys.length > 0 && (
                        <>
                            <div className="layers-type-section">
                                <span>{mapSettingsAdditionalLayers?.name || 'ДОПОЛНИТЕЛЬНЫЕ СЛОИ'}</span>
                            </div>
                            <div className="layers-list">
                                {enabledAdditionalLayerKeys.map((layerName: string) => (
                                    <div key={layerName} className='layers-list-item'>
                                        <label className='checkbox-label'>{layersDict?.[layerName] ?? layerName}</label>
                                        <div className='opacity-control'>
                                            <Slider
                                                value={Math.round((additionalLayersOpacityState?.[layerName] ?? 1) * 100)}
                                                onChange={(e: any) => onChangeLayerOpacity(layerName, (e.value ?? 100) / 100)}
                                                onSlideEnd={commitAdditionalOpacity}
                                                step={1} min={0} max={100}
                                            />
                                            <span className='opacity-percent'>{Math.round((additionalLayersOpacityState?.[layerName] ?? 1) * 100)}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            }
        </div>
    );
}
